import jsPDF from 'jspdf';
import type { PublicPlan, AIGeneratedContent, QuestionnaireData } from '@data/publicPlans';
import { stylePresetsByKey } from '@data/stylePresets';
import { getImageUrl, getPdfFetchableImageUrl } from './imageUrl';
import type { GanttTask } from '../types/gantt';
import type { Offer } from '@data/offers';
import { generateSyntheticGanttData, ganttTasksFromPlanInstructions } from '@utils/ganttDataGenerator';
import type { Language } from '../i18n';
import { formatPdfDate, getPdfLabels, type PdfLabels, resolvePdfLanguage } from './pdfLabels';
import { normalizePricingBreakdown } from './pricingBreakdownUtils';

interface PDFOptions {
	plan: PublicPlan;
	aiContent: AIGeneratedContent;
	questionnaire: QuestionnaireData;
	language?: Language;
}

type PdfImageFormat = 'JPEG' | 'PNG' | 'WEBP';

function detectPdfImageFormat(dataUrl: string): PdfImageFormat {
	if (dataUrl.includes('image/png')) return 'PNG';
	if (dataUrl.includes('image/webp')) return 'WEBP';
	return 'JPEG';
}

function loadImageViaCanvas(url: string): Promise<string> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.onload = () => {
			try {
				const canvas = document.createElement('canvas');
				canvas.width = img.naturalWidth;
				canvas.height = img.naturalHeight;
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					reject(new Error('Canvas context unavailable'));
					return;
				}
				ctx.drawImage(img, 0, 0);
				resolve(canvas.toDataURL('image/jpeg', 0.92));
			} catch (error) {
				reject(error);
			}
		};
		img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
		img.src = url;
	});
}

/**
 * Converts an image URL (including data URLs) to a base64 data URL
 */
async function imageToBase64(url: string): Promise<string> {
	if (url.startsWith('data:')) {
		return url;
	}

	const fetchUrl = getPdfFetchableImageUrl(url) ?? url;

	try {
		const response = await fetch(fetchUrl, { mode: 'cors', credentials: 'include' });
		if (!response.ok) {
			throw new Error(`Image fetch failed (${response.status})`);
		}
		const blob = await response.blob();
		const dataUrl = await new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
		// Proxy returns JPEG; normalize for jsPDF.
		if (dataUrl.includes('image/jpeg') || dataUrl.includes('image/jpg')) {
			return dataUrl;
		}
		return await loadImageViaCanvas(dataUrl);
	} catch (error) {
		console.warn('Fetch image failed, trying canvas fallback:', error);
		try {
			return await loadImageViaCanvas(getImageUrl(url) ?? url);
		} catch (canvasError) {
			console.error('Failed to load image:', canvasError);
			return '';
		}
	}
}

function resolveOfferBeforeAfterUrls(plan: PublicPlan, offer: Offer, aiContent: AIGeneratedContent) {
	const snapshot = offer.planSnapshot;
	const beforeRawUrl =
		snapshot?.questionnaire?.photoBeforeUrl ??
		snapshot?.photoBeforeUrl ??
		plan.questionnaire?.photoBeforeUrl ??
		plan.photoBeforeUrl ??
		'';
	const isPlaceholderAfter = Boolean(
		aiContent.photoAfterUrl && String(aiContent.photoAfterUrl).includes('unsplash.com')
	);
	const afterRawUrl = !isPlaceholderAfter
		? (aiContent.photoAfterUrl ?? '')
		: beforeRawUrl;

	return {
		beforeUrl: beforeRawUrl ? getImageUrl(beforeRawUrl) ?? '' : '',
		afterUrl: afterRawUrl ? getImageUrl(afterRawUrl) ?? '' : ''
	};
}

/**
 * Adds a page header with title and page number
 */
function addPageHeader(doc: jsPDF, title: string, pageNum: number, totalPages: number) {
	// Intentionally NO-OP.
	// We render the header once for every page at the end (after total page count is known).
	// If we draw here as well, jsPDF can't remove old text, so titles/page numbers overlap.
}

/**
 * Adds a section title
 */
function addSectionTitle(doc: jsPDF, title: string, y: number): number {
	doc.setFontSize(16);
	doc.setTextColor(25, 135, 84); // Green color
	doc.setFont('helvetica', 'bold');
	doc.text(title, 14, y);
	doc.setDrawColor(25, 135, 84);
	doc.line(14, y + 2, doc.internal.pageSize.getWidth() - 14, y + 2);
	return y + 8;
}

/**
 * Adds text with word wrapping
 */
function addWrappedText(doc: jsPDF, text: string, x: number, y: number, maxWidth: number, fontSize: number = 10): number {
	doc.setFontSize(fontSize);
	doc.setTextColor(0, 0, 0);
	doc.setFont('helvetica', 'normal');
	
	const lines = doc.splitTextToSize(text, maxWidth);
	doc.text(lines, x, y);
	// Conservative line height to reduce vertical overlaps.
	return y + (lines.length * fontSize * 0.45);
}

/**
 * Adds a key-value pair
 */
function addKeyValue(doc: jsPDF, key: string, value: string, x: number, y: number, maxWidth: number): number {
	doc.setFontSize(9);
	doc.setTextColor(100, 100, 100);
	doc.setFont('helvetica', 'normal');
	doc.text(key, x, y);
	
	doc.setFontSize(10);
	doc.setTextColor(0, 0, 0);
	doc.setFont('helvetica', 'bold');
	const valueLines = doc.splitTextToSize(value, maxWidth);
	doc.text(valueLines, x, y + 5);
	
	// Conservative per-line height for value lines.
	return y + 5 + (valueLines.length * 4.5);
}

function startOfWeekMonday(date: Date): Date {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	// JS: 0=Sun...6=Sat. Convert so Monday=0..Sunday=6
	const day = (d.getDay() + 6) % 7;
	d.setDate(d.getDate() - day);
	return d;
}

function truncateText(text: string, maxLen: number): string {
	if (text.length <= maxLen) return text;
	return `${text.slice(0, Math.max(0, maxLen - 3))}...`;
}

function formatDateShort(iso?: string | null, language?: Language): string {
	return formatPdfDate(iso ?? undefined, language);
}

function sanitizeFileName(input: string): string {
	return input.replace(/[^a-z0-9]/gi, '_');
}

function toDatePart(iso?: string | null): string {
	if (!iso) return new Date().toISOString().split('T')[0];
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return new Date().toISOString().split('T')[0];
	return d.toISOString().split('T')[0];
}

function toDatePartDDMMYYYY(iso?: string | null): string {
	const yyyyMmDd = toDatePart(iso);
	const [yyyy, mm, dd] = yyyyMmDd.split('-');
	if (!yyyy || !mm || !dd) return yyyyMmDd;
	return `${dd}-${mm}-${yyyy}`;
}

function stableStringify(value: any): string {
	if (value === undefined) return '"__undefined__"';
	if (value === null) return 'null';
	if (typeof value === 'string') return JSON.stringify(value);
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	if (typeof value !== 'object') return JSON.stringify(value);

	if (Array.isArray(value)) {
		return `[${value.map(stableStringify).join(',')}]`;
	}

	const keys = Object.keys(value).sort();
	return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

function hashStringToShort(input: string, length: number = 8): string {
	// Not cryptographically secure; just needs stable-ish short uniqueness.
	let h1 = 0xdeadbeef ^ input.length;
	let h2 = 0x41c6ce57 ^ input.length;
	for (let i = 0; i < input.length; i++) {
		const ch = input.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 2654435761);
		h2 = Math.imul(h2 ^ ch, 1597334677);
	}
	h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
	h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

	const result = 4294967296 * (2097151 & h2) + (h1 >>> 0);
	return result.toString(36).slice(0, length);
}

function getAppHomepageUrl(): string {
	const envUrl =
		(import.meta as any).env?.VITE_APP_HOMEPAGE_URL ||
		(import.meta as any).env?.VITE_PUBLIC_SITE_URL ||
		(import.meta as any).env?.VITE_SITE_URL;
	if (typeof envUrl === 'string' && envUrl.trim()) {
		const trimmed = envUrl.trim();
		return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
	}
	// Production default.
	return 'https://hortera.org/';
}

function addClickableAppHomepageFooter(
	doc: jsPDF,
	{
		pageWidth,
		pageHeight,
		margin,
		contentWidth,
		url,
		linkText
	}: {
		pageWidth: number;
		pageHeight: number;
		margin: number;
		contentWidth: number;
		url: string;
		linkText: string;
	}
) {
	const footerY = pageHeight - 12;
	doc.setFontSize(9);
	doc.setTextColor(25, 135, 84);
	doc.setFont('helvetica', 'normal');
	doc.text(linkText, margin, footerY);
	doc.link(margin, footerY - 3, contentWidth, 4.5, { url });
}

function addClickableAppHomepageLink(
	doc: jsPDF,
	{
		x,
		y,
		width,
		url,
		linkText
	}: {
		x: number;
		y: number;
		width: number;
		url: string;
		linkText: string;
	}
) {
	doc.setFontSize(9);
	doc.setTextColor(25, 135, 84);
	doc.setFont('helvetica', 'normal');
	doc.text(linkText, x, y);
	// Rectangle for the clickable area: left/top/width/height
	doc.link(x, y - 3, width, 4.5, { url });
}

/**
 * Adds a compact "draft" Gantt chart to the PDF (simple bars + week axis).
 * This intentionally keeps layout deterministic to avoid overlap issues with HTML-to-canvas.
 */
function addSimpleGanttChart(
	doc: jsPDF,
	tasks: GanttTask[],
	x: number,
	y: number,
	width: number,
	pageHeight: number,
	margin: number,
	labels: Pick<PdfLabels, 'weeks' | 'task' | 'dateLocale'>
): number {
	if (!tasks || tasks.length === 0) return y;

	const nameWidth = Math.min(52, Math.max(40, width * 0.28));
	const timelineWidth = width - nameWidth;
	// Slightly larger row height reduces perceived overlap between labels and bars.
	const rowHeight = 8;
	const headerHeight = 12;
	// Reserve some space at the top for the PDF header line (drawn around y=25).
	const pageTopY = margin + 18;

	const msPerDay = 24 * 60 * 60 * 1000;
	const msPerWeek = msPerDay * 7;

	const taskDates = tasks.flatMap(t => [new Date(t.startDate), new Date(t.endDate)]);
	const minDate = new Date(Math.min(...taskDates.map(d => d.getTime())));
	const maxDate = new Date(Math.max(...taskDates.map(d => d.getTime())));

	const axisStart = startOfWeekMonday(minDate);
	const axisEndStart = startOfWeekMonday(maxDate);
	// Use a continuous day scale across the whole visible range (end of the last week),
	// so bars align more precisely and don't look "simultaneous" due to week rounding.
	const axisEnd = new Date(axisEndStart.getTime() + msPerWeek - msPerDay); // end of last week (Sunday)
	const totalDays = Math.max(1, Math.round((axisEnd.getTime() - axisStart.getTime()) / msPerDay) + 1);
	const dayScale = timelineWidth / totalDays;
	const timelineX = x + nameWidth;

	let idx = 0;
	let currentY = Math.max(y, pageTopY);

	while (idx < tasks.length) {
		// How many rows can we fit on this page starting at currentY?
		const availableHeight = (pageHeight - margin) - currentY;
		const rowsFit = Math.max(1, Math.floor((availableHeight - headerHeight - 4) / rowHeight));
		const pageTasks = tasks.slice(idx, idx + rowsFit);
		const requiredHeight = headerHeight + (pageTasks.length * rowHeight) + 4;

		// If even the header doesn't fit, go to next page.
		if (requiredHeight > availableHeight) {
			doc.addPage();
			currentY = pageTopY;
			continue;
		}

		// Outer frame
		doc.setDrawColor(25, 135, 84);
		doc.rect(x, currentY, width, requiredHeight - 2, 'S');

		// Week axis labels
		doc.setFont('helvetica', 'normal');
		doc.setFontSize(8);
		doc.setTextColor(60, 60, 60);
		doc.text(labels.weeks, x + 2, currentY + 6);

		// Axis labels: show a reduced set of week markers.
		const maxWeeks = Math.max(1, Math.ceil(totalDays / 7));
		const labelEvery = Math.max(1, Math.ceil(maxWeeks / 4));
		for (let w = 0; w < maxWeeks; w++) {
			if (w % labelEvery !== 0 && w !== maxWeeks - 1) continue;
			const weekStart = new Date(axisStart.getTime() + w * 7 * msPerDay);
			const label = weekStart.toLocaleDateString(labels.dateLocale, { day: '2-digit', month: 'short' });
			const xPos = timelineX + (w * 7 * dayScale) + 1;
			doc.text(label, xPos, currentY + 6);
		}

		// Bars for current page rows
		for (let i = 0; i < pageTasks.length; i++) {
			const t = pageTasks[i];
			const rowTop = currentY + headerHeight + (i * rowHeight);

			doc.setTextColor(0, 0, 0);
			doc.setFontSize(7);
			// Place label a bit higher so it doesn't collide with the bar.
			doc.text(truncateText(t.name || labels.task, 28), x + 2, rowTop + 5);

			const taskStart = new Date(t.startDate);
			const taskEnd = new Date(t.endDate);

			const startDay = Math.floor((taskStart.getTime() - axisStart.getTime()) / msPerDay);
			const endDay = Math.floor((taskEnd.getTime() - axisStart.getTime()) / msPerDay);
			const clampedStartDay = Math.max(0, Math.min(totalDays - 1, startDay));
			const clampedEndDay = Math.max(0, Math.min(totalDays - 1, endDay));

			let fill = { r: 210, g: 235, b: 220 };
			let stroke = { r: 25, g: 135, b: 84 };
			const status = t.status ?? 'not_started';
			if (status === 'in_progress') {
				fill = { r: 255, g: 220, b: 140 };
				stroke = { r: 198, g: 140, b: 0 };
			} else if (status === 'completed') {
				fill = { r: 25, g: 135, b: 84 };
				stroke = { r: 25, g: 135, b: 84 };
			} else if (status === 'delayed' || status === 'blocked') {
				fill = { r: 240, g: 160, b: 160 };
				stroke = { r: 220, g: 53, b: 69 };
			}

			// Thinner bars help prevent visual overlap in dense charts.
			const barH = 3;
			const barY = rowTop + ((rowHeight - barH) / 2);
			const rawBarW = (clampedEndDay - clampedStartDay + 1) * dayScale;
			const barX = timelineX + (clampedStartDay * dayScale) + 1;
			const barW = Math.max(1.2, rawBarW - 2);

			doc.setFillColor(fill.r, fill.g, fill.b);
			doc.setDrawColor(stroke.r, stroke.g, stroke.b);
			doc.rect(barX, barY, barW, barH, 'F');
			doc.rect(barX, barY, barW, barH, 'S');

			// Row separator for readability
			doc.setDrawColor(230, 230, 230);
			doc.line(x, rowTop + rowHeight, x + width, rowTop + rowHeight);
		}

		idx += pageTasks.length;
		currentY = currentY + requiredHeight;

		// If there are more tasks, go to next page for the next chart chunk.
		if (idx < tasks.length) {
			doc.addPage();
			currentY = pageTopY;
		}
	}

	return currentY;
}

/**
 * Generates a comprehensive PDF from plan data
 */
export async function generatePlanPDF({ plan, aiContent: initialAiContent, questionnaire, language }: PDFOptions): Promise<void> {
	const L = getPdfLabels(language);
	const aiContent: AIGeneratedContent = initialAiContent.pricingBreakdown
		? { ...initialAiContent, pricingBreakdown: normalizePricingBreakdown(initialAiContent.pricingBreakdown) }
		: initialAiContent;
	const doc = new jsPDF('p', 'mm', 'a4');
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	const margin = 14;
	const contentWidth = pageWidth - (margin * 2);
	
	// Start a bit lower to avoid overlapping with the compact PDF header.
	let y = margin + 18;
	const style = stylePresetsByKey[plan.styleKey];
	const appHomepageUrl = getAppHomepageUrl();
	const appHomepageLinkText = L.tryHortEra;
	const generatedDate = new Date().toLocaleDateString(L.dateLocale);
	
	// Title Page
	doc.setFontSize(24);
	doc.setTextColor(25, 135, 84);
	doc.setFont('helvetica', 'bold');
	doc.text(plan.title, margin, y);
	
	y += 15;
	doc.setFontSize(12);
	doc.setTextColor(100, 100, 100);
	doc.setFont('helvetica', 'normal');
	doc.text(L.generatedOn(generatedDate), margin, y);
	addClickableAppHomepageLink(doc, {
		x: margin,
		y: y + 6,
		width: contentWidth,
		url: appHomepageUrl,
		linkText: appHomepageLinkText
	});
	
	y += 20;
	
	// Plan Overview Section
	y = addSectionTitle(doc, L.planOverview, y);
	
	y += 5;
	const startY = y;
	y = addKeyValue(doc, L.style, style?.title || plan.styleKey, margin, y, contentWidth / 2 - 5);
	y = addKeyValue(doc, L.gardenSize, `${questionnaire.gardenSize} m²`, pageWidth / 2 + 5, startY, contentWidth / 2 - 5);
	y = Math.max(y, startY + 10);
	y = addKeyValue(doc, L.location, plan.city || questionnaire.location, margin, y, contentWidth);
	y += 5;
	
	// Description
	doc.setFontSize(9);
	doc.setTextColor(100, 100, 100);
	doc.setFont('helvetica', 'normal');
	doc.text(L.description, margin, y);
	y += 5;
	y = addWrappedText(doc, aiContent.planDescription, margin, y, contentWidth, 10);
	y += 10;
	
	// Before/After Images
	const beforeAfterImgHeight = 60;
	const beforeAfterImgPadding = 10;
	if (y > pageHeight - 80) {
		doc.addPage();
		y = margin + 10;
		addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
		y += 15;
	}
	
	y = addSectionTitle(doc, L.beforeAfter, y);
	y += 5;
	
	// Before Image
	const rawBeforeImageUrl = questionnaire.photoBeforeUrl || plan.photoBeforeUrl || '';
	const beforeImageUrl = getImageUrl(rawBeforeImageUrl);
	// Prefer a real AI-generated "after" image. If it's missing or just an Unsplash placeholder,
	// fall back to the same source as the "before" image so the PDF always shows something.
	const isPlaceholderAfter =
		!!aiContent.photoAfterUrl && aiContent.photoAfterUrl.includes('unsplash.com');
	const rawAfterImageUrl = !isPlaceholderAfter && aiContent.photoAfterUrl
		? aiContent.photoAfterUrl
		: rawBeforeImageUrl;
	const afterImageUrl = rawAfterImageUrl ? getImageUrl(rawAfterImageUrl) : '';
	
	if (beforeImageUrl) {
		try {
			const beforeBase64 = await imageToBase64(beforeImageUrl);
			if (beforeBase64) {
				doc.setFontSize(8);
				doc.setTextColor(100, 100, 100);
				doc.text(L.currentState, margin, y);
				y += 3;
				
				const imgWidth = (contentWidth - 5) / 2;
				const imgHeight = beforeAfterImgHeight; // Slightly taller images for better visibility
				doc.addImage(beforeBase64, 'JPEG', margin, y, imgWidth, imgHeight);
			}
		} catch (error) {
			console.error('Failed to add before image:', error);
		}
	}
	
	// After Image
	if (afterImageUrl) {
		try {
			const afterBase64 = await imageToBase64(afterImageUrl);
			if (afterBase64) {
				doc.setFontSize(8);
				doc.setTextColor(100, 100, 100);
				doc.text(L.designVision, pageWidth / 2 + 5, y - 3);
				
				const imgWidth = (contentWidth - 5) / 2;
				const imgHeight = beforeAfterImgHeight;
				doc.addImage(afterBase64, 'JPEG', pageWidth / 2 + 5, y, imgWidth, imgHeight);
			}
		} catch (error) {
			console.error('Failed to add after image:', error);
		}
	}
	
	// Advance past the image block (imgHeight is 60mm, so 55mm previously caused overlaps).
	y += beforeAfterImgHeight + beforeAfterImgPadding;
	
	// Conceptual Plan Section
	if (y > pageHeight - 60) {
		doc.addPage();
		y = margin + 10;
		addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
		y += 15;
	}
	
	y = addSectionTitle(doc, L.conceptualPlan, y);
	y += 5;
	
	// Key Features
	doc.setFontSize(12);
	doc.setTextColor(0, 0, 0);
	doc.setFont('helvetica', 'bold');
	doc.text(L.keyFeatures, margin, y);
	y += 8;
	
	aiContent.keyFeatures.forEach((feature, idx) => {
		if (y > pageHeight - 30) {
			doc.addPage();
			y = margin + 10;
			addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
			y += 15;
		}
		
		doc.setFontSize(10);
		doc.setTextColor(25, 135, 84);
		doc.setFont('helvetica', 'bold');
		doc.text('✓', margin, y);
		doc.setTextColor(0, 0, 0);
		doc.setFont('helvetica', 'normal');
		y = addWrappedText(doc, feature, margin + 5, y, contentWidth - 5, 10);
		y += 3;
	});
	
	y += 5;
	
	// Project Plan Section
	if (y > pageHeight - 60) {
		doc.addPage();
		y = margin + 10;
		addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
		y += 15;
	}
	
	y = addSectionTitle(doc, L.projectPlan, y);
	y += 5;
	
	// Implementation Instructions
	doc.setFontSize(12);
	doc.setTextColor(0, 0, 0);
	doc.setFont('helvetica', 'bold');
	doc.text(L.implementationInstructions, margin, y);
	y += 8;
	
	aiContent.instructions.forEach((instruction, idx) => {
		if (y > pageHeight - 30) {
			doc.addPage();
			y = margin + 10;
			addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
			y += 15;
		}
		
		doc.setFontSize(10);
		doc.setTextColor(0, 0, 0);
		doc.setFont('helvetica', 'bold');
		const colonIndex = instruction.indexOf(':');
		if (colonIndex > 0) {
			const title = instruction.substring(0, colonIndex);
			const description = instruction.substring(colonIndex + 1).trim();
			// Keep implementation steps consistent (no green sub-titles).
			doc.setTextColor(0, 0, 0);
			doc.text(`${idx + 1}. ${title}:`, margin, y);
			doc.setTextColor(0, 0, 0);
			doc.setFont('helvetica', 'normal');
			y = addWrappedText(doc, description, margin + 5, y + 5, contentWidth - 5, 10);
		} else {
			doc.text(`${idx + 1}.`, margin, y);
			doc.setFont('helvetica', 'normal');
			y = addWrappedText(doc, instruction, margin + 10, y, contentWidth - 10, 10);
		}
		y += 3;
	});
	
	y += 5;
	
	// Required Tools
	if (aiContent.tools && aiContent.tools.length > 0) {
		if (y > pageHeight - 50) {
			doc.addPage();
			y = margin + 10;
			addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
			y += 15;
		}
		
		doc.setFontSize(12);
		doc.setTextColor(0, 0, 0);
		doc.setFont('helvetica', 'bold');
		doc.text(L.requiredTools, margin, y);
		y += 8;
		
		if (typeof aiContent.tools[0] === 'string') {
			(aiContent.tools as string[]).forEach((tool) => {
				if (y > pageHeight - 20) {
					doc.addPage();
					y = margin + 10;
					addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
					y += 15;
				}
				doc.setFontSize(10);
				doc.setTextColor(25, 135, 84);
				doc.setFont('helvetica', 'bold');
				doc.text('•', margin, y);
				doc.setTextColor(0, 0, 0);
				doc.setFont('helvetica', 'normal');
				y = addWrappedText(doc, tool, margin + 5, y, contentWidth - 5, 10);
				y += 3;
			});
		} else {
			(aiContent.tools as Array<{ name: string; description: string }>).forEach((tool) => {
				if (y > pageHeight - 30) {
					doc.addPage();
					y = margin + 10;
					addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
					y += 15;
				}
				doc.setFontSize(10);
				doc.setTextColor(0, 0, 0);
				doc.setFont('helvetica', 'bold');
				doc.text(`• ${tool.name}`, margin, y);
				y += 5;
				doc.setFont('helvetica', 'normal');
				doc.setTextColor(100, 100, 100);
				y = addWrappedText(doc, tool.description, margin + 5, y, contentWidth - 5, 9);
				y += 3;
			});
		}
		y += 5;
	}
	
	// Materials List
	if (y > pageHeight - 50) {
		doc.addPage();
		y = margin + 10;
		addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
		y += 15;
	}
	
	doc.setFontSize(12);
	doc.setTextColor(0, 0, 0);
	doc.setFont('helvetica', 'bold');
	doc.text(L.materials, margin, y);
	y += 8;
	
	aiContent.pricingBreakdown.materials.forEach((material) => {
		if (y > pageHeight - 25) {
			doc.addPage();
			y = margin + 10;
			addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
			y += 15;
		}
		
		doc.setFontSize(10);
		doc.setTextColor(0, 0, 0);
		doc.setFont('helvetica', 'bold');
		doc.text(material.name, margin, y);
		doc.setFont('helvetica', 'normal');
		doc.text(L.materialLine(material.quantity, material.unit, material.unitPrice, material.unit), margin + 5, y + 5);
		doc.setFont('helvetica', 'bold');
		doc.setTextColor(25, 135, 84);
		doc.text(`${material.total.toLocaleString(L.dateLocale)} EUR`, pageWidth - margin - 20, y, { align: 'right' });
		y += 10;
	});
	
	y += 5;
	
	// Detailed Plan Section
	if (y > pageHeight - 60) {
		doc.addPage();
		y = margin + 10;
		addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
		y += 15;
	}
	
	y = addSectionTitle(doc, L.detailedPlan, y);
	y += 5;
	
	// Estimated Pricing
	doc.setFontSize(12);
	doc.setTextColor(0, 0, 0);
	doc.setFont('helvetica', 'bold');
	doc.text(L.estimatedPricing, margin, y);
	y += 8;
	
	doc.setFontSize(10);
	doc.setFont('helvetica', 'normal');
	doc.setTextColor(0, 0, 0);
	doc.setFont('helvetica', 'normal');
	doc.setTextColor(0, 0, 0);
	doc.text(L.estimatedMid, margin, y);
	doc.setFont('helvetica', 'bold');
	doc.setTextColor(25, 135, 84);
	doc.text(`${aiContent.pricingBreakdown.mid.toLocaleString(L.dateLocale)} EUR`, pageWidth - margin - 20, y, { align: 'right' });
	y += 10;
	
	// Labor
	doc.setFontSize(12);
	doc.setFont('helvetica', 'bold');
	doc.setTextColor(0, 0, 0);
	doc.text(L.labor, margin, y);
	y += 8;
	
	aiContent.pricingBreakdown.labor.forEach((labor) => {
		if (y > pageHeight - 20) {
			doc.addPage();
			y = margin + 10;
			addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
			y += 15;
		}
		
		doc.setFontSize(10);
		doc.setFont('helvetica', 'normal');
		doc.text(labor.name, margin, y);
		doc.setFont('helvetica', 'bold');
		doc.text(`${labor.total.toLocaleString(L.dateLocale)} EUR`, pageWidth - margin - 20, y, { align: 'right' });
		y += 7;
	});
	
	// Timeline if available
	if (questionnaire.timeline) {
		y += 5;
		if (y > pageHeight - 40) {
			doc.addPage();
			y = margin + 10;
			addPageHeader(doc, plan.title, doc.getNumberOfPages(), 0);
			y += 15;
		}
		
		doc.setFontSize(12);
		doc.setFont('helvetica', 'bold');
		doc.setTextColor(0, 0, 0);
		doc.text(L.timeline, margin, y);
		y += 8;
		
		doc.setFontSize(10);
		doc.setFont('helvetica', 'normal');
		y = addWrappedText(doc, questionnaire.timeline, margin, y, contentWidth, 10);
	}

	// Draft Gantt chart (try parsing instruction days; fall back to synthetic if parsing yields nothing)
	const instructions = aiContent.instructions ?? [];
	const startDate = questionnaire.projectStartDate
		? new Date(questionnaire.projectStartDate)
		: plan.createdAt
			? new Date(plan.createdAt)
			: new Date();
	let ganttTasks: GanttTask[] = [];
	if (instructions.length) {
		ganttTasks = ganttTasksFromPlanInstructions(instructions, startDate);
	}
	if (!ganttTasks.length) {
		ganttTasks = generateSyntheticGanttData('garden', aiContent.pricingBreakdown?.mid ?? 0, startDate);
	}

	if (ganttTasks.length > 0) {
		// Try to keep the section title and chart on the same page.
		if (y + 20 > pageHeight - margin) {
			doc.addPage();
			y = margin + 10;
		}
		y += 6;
		doc.setFontSize(12);
		doc.setTextColor(0, 0, 0);
		doc.setFont('helvetica', 'bold');
		doc.text(L.ganttTimelineDraft, margin, y);
		y += 6;
		y = addSimpleGanttChart(doc, ganttTasks, margin, y, contentWidth, pageHeight, margin, L);
	}
	
	// Update page numbers in headers
	const totalPages = doc.getNumberOfPages();
	const headerTitleY = 20;
	const headerLineY = 25;
	const firstPageNumberY = 35; // avoid overlap with the large title on the cover page
	for (let i = 1; i <= totalPages; i++) {
		doc.setPage(i);
		doc.setFontSize(10);
		doc.setTextColor(100, 100, 100);
		const pageNumberY = i === 1 ? firstPageNumberY : headerTitleY;

		// Cover page: we already render the large title at the top, so don't draw the compact header title/line.
		if (i !== 1) {
			doc.text(plan.title, margin, headerTitleY);
			doc.setDrawColor(200, 200, 200);
			doc.line(margin, headerLineY, pageWidth - margin, headerLineY);
		}

		doc.text(L.pageOf(i, totalPages), pageWidth - margin, pageNumberY, { align: 'right' });
	}
	
	// Save the PDF
	const datePart = toDatePartDDMMYYYY(aiContent.generatedAt || plan.createdAt);
	const contentHash = hashStringToShort(
		stableStringify({
			planId: plan.databaseId ?? plan.id,
			title: plan.title,
			styleKey: plan.styleKey,
			questionnaire: {
				gardenSize: questionnaire.gardenSize,
				gardenShape: questionnaire.gardenShape,
				needsWants: questionnaire.needsWants,
				maintenanceLevel: questionnaire.maintenanceLevel,
				childrenPets: questionnaire.childrenPets,
				budgetRange: questionnaire.budgetRange,
				timeline: questionnaire.timeline,
				location: questionnaire.location,
				projectStartDate: questionnaire.projectStartDate
			},
			ai: {
				generatedAt: aiContent.generatedAt,
				planDescription: aiContent.planDescription,
				keyFeatures: aiContent.keyFeatures,
				instructions: aiContent.instructions
			}
		}),
		8
	);
	const fileName = `HortEra-Plan-${plan.databaseId ?? plan.id}-${datePart}-${contentHash}.pdf`;
	doc.save(fileName);
}

type OfferPDFOptions = {
	plan: PublicPlan;
	offer: Offer;
	language?: Language;
};

type ProjectOfferMeta = {
	bands?: { min: number; mid: number; max: number };
	message?: string;
	validUntil?: string;
	projectStartDate?: string;
	timelineEstimate?: string;
};

type ProjectPDFOptions = {
	projectTitle: string;
	projectStatusLabel: string;
	gardenVision: AIGeneratedContent;
	ganttTasks: GanttTask[];
	offerMeta?: ProjectOfferMeta;
	client?: string;
	companyName?: string;
	budget?: number;
	dueDate?: string;
	beforeImageUrl?: string | null;
	afterImageUrl?: string | null;
	/**
	 * Controls how much detail to render for task list.
	 * - seeker: shorter / less dense
	 * - company: full tasks + extra task info
	 */
	detailLevel?: 'seeker' | 'company';
	language?: Language;
};

/**
 * Generate a PDF for a specific marketplace offer.
 * Keeps it intentionally similar to the seeker plan PDF, but includes offer-specific pricing/timeline.
 */
export async function generateOfferPDF({ plan, offer, language }: OfferPDFOptions): Promise<void> {
	const L = getPdfLabels(language);
	const doc = new jsPDF('p', 'mm', 'a4');
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	const margin = 14;
	const contentWidth = pageWidth - margin * 2;

	const questionnaire = plan.questionnaire;
	const style = stylePresetsByKey[plan.styleKey];

	const aiContent: AIGeneratedContent | undefined = (offer.planSnapshot?.aiGenerated as AIGeneratedContent | undefined)
		?? plan.aiGenerated;
	if (!aiContent) throw new Error('Offer PDF generation: missing AI content.');

	const docTitle = offer.gardenerName
		? `${plan.title} - ${L.offerTitleSuffix} (${offer.gardenerName})`
		: `${plan.title} - ${L.offerTitleSuffix}`;

	const { beforeUrl, afterUrl } = resolveOfferBeforeAfterUrls(plan, offer, aiContent);

	let y = margin + 10;
	const appHomepageUrl = getAppHomepageUrl();
	const appHomepageLinkText = L.tryHortEra;
	const generatedDate = new Date().toLocaleDateString(L.dateLocale);

	// Title Page
	doc.setFontSize(24);
	doc.setTextColor(25, 135, 84);
	doc.setFont('helvetica', 'bold');
	const titleLines = doc.splitTextToSize(docTitle, contentWidth) as string[];
	const titleLineHeight = 11;
	titleLines.forEach((line, idx) => {
		doc.text(String(line), margin, y + (idx * titleLineHeight));
	});

	y += 15 + Math.max(0, titleLines.length - 1) * titleLineHeight;
	doc.setFontSize(12);
	doc.setTextColor(100, 100, 100);
	doc.setFont('helvetica', 'normal');
	doc.text(L.generatedOn(generatedDate), margin, y);
	addClickableAppHomepageLink(doc, {
		x: margin,
		y: y + 6,
		width: contentWidth,
		url: appHomepageUrl,
		linkText: appHomepageLinkText
	});
	y += 20;

	// Plan Overview
	y = addSectionTitle(doc, L.planOverview, y);
	y += 5;
	const startY = y;

	if (style?.title) {
		y = addKeyValue(doc, L.style, style.title, margin, y, contentWidth / 2 - 5);
	} else {
		y = addKeyValue(doc, L.style, plan.styleKey, margin, y, contentWidth / 2 - 5);
	}

	const gardenSizeText = questionnaire ? `${questionnaire.gardenSize} m²` : '';
	if (gardenSizeText) {
		y = addKeyValue(doc, L.gardenSize, gardenSizeText, pageWidth / 2 + 5, startY, contentWidth / 2 - 5);
	}

	y = Math.max(y, startY + 10);
	y = addKeyValue(doc, L.location, plan.city || questionnaire?.location || '-', margin, y, contentWidth);
	y += 5;

	// Description / Vision
	doc.setFontSize(9);
	doc.setTextColor(100, 100, 100);
	doc.setFont('helvetica', 'normal');
	doc.text(L.description, margin, y);
	y += 5;
	y = addWrappedText(doc, aiContent.planDescription, margin, y, contentWidth, 10);
	y += 10;

	// Before / After images
	const beforeAfterImgHeight = 60;
	const beforeAfterImgPadding = 10;
	if (y > pageHeight - 80) {
		doc.addPage();
		y = margin + 22;
	}

	y = addSectionTitle(doc, L.beforeAfter, y);
	y += 5;

	if (beforeUrl) {
		try {
			const beforeBase64 = await imageToBase64(beforeUrl);
			if (beforeBase64) {
				doc.setFontSize(8);
				doc.setTextColor(100, 100, 100);
				doc.text(L.currentState, margin, y);
				y += 3;
				const imgWidth = (contentWidth - 5) / 2;
				doc.addImage(beforeBase64, detectPdfImageFormat(beforeBase64), margin, y, imgWidth, beforeAfterImgHeight);
			}
		} catch (error) {
			console.error('Failed to add before image:', error);
		}
	}

	// After image uses the same y baseline (reset back by the label increment above).
	const afterBaselineY = y;
	if (afterUrl) {
		try {
			const afterBase64 = await imageToBase64(afterUrl);
			if (afterBase64) {
				doc.setFontSize(8);
				doc.setTextColor(100, 100, 100);
				doc.text(L.offerVision, pageWidth / 2 + 5, afterBaselineY - 3);
				const imgWidth = (contentWidth - 5) / 2;
				doc.addImage(afterBase64, detectPdfImageFormat(afterBase64), pageWidth / 2 + 5, afterBaselineY, imgWidth, beforeAfterImgHeight);
			}
		} catch (error) {
			console.error('Failed to add after image:', error);
		}
	}

	y = afterBaselineY + beforeAfterImgHeight + beforeAfterImgPadding;

	// Key features
	if (y > pageHeight - 60) {
		doc.addPage();
		y = margin + 22;
	}

	y = addSectionTitle(doc, L.keyFeatures, y);
	y += 5;
	aiContent.keyFeatures.forEach((feature) => {
		if (y > pageHeight - 30) {
			doc.addPage();
			y = margin + 22;
		}
		doc.setFontSize(10);
		doc.setTextColor(25, 135, 84);
		doc.setFont('helvetica', 'bold');
		doc.text('✓', margin, y);
		doc.setTextColor(0, 0, 0);
		doc.setFont('helvetica', 'normal');
		y = addWrappedText(doc, feature, margin + 5, y, contentWidth - 5, 10);
		y += 2;
	});

	// Implementation steps
	y += 3;
	if (y > pageHeight - 60) {
		doc.addPage();
		y = margin + 22;
	}

	y = addSectionTitle(doc, L.implementationSteps, y);
	y += 5;
	aiContent.instructions.forEach((instruction, idx) => {
		if (y > pageHeight - 30) {
			doc.addPage();
			y = margin + 22;
		}

		doc.setFontSize(10);
		doc.setTextColor(0, 0, 0);
		doc.setFont('helvetica', 'bold');

		const colonIndex = instruction.indexOf(':');
		if (colonIndex > 0) {
			const title = instruction.substring(0, colonIndex);
			const description = instruction.substring(colonIndex + 1).trim();
			doc.setTextColor(25, 135, 84);
			doc.text(`${idx + 1}. ${title}:`, margin, y);
			doc.setTextColor(0, 0, 0);
			doc.setFont('helvetica', 'normal');
			y = addWrappedText(doc, description, margin + 5, y + 5, contentWidth - 5, 10);
		} else {
			doc.text(`${idx + 1}.`, margin, y);
			doc.setFont('helvetica', 'normal');
			y = addWrappedText(doc, instruction, margin + 10, y, contentWidth - 10, 10);
		}

		y += 2;
	});

	// Offer details
	y += 5;
	if (y > pageHeight - 60) {
		doc.addPage();
		y = margin + 22;
	}

	y = addSectionTitle(doc, L.offerDetails, y);
	y += 5;
	const offerPrice = (offer.bands.mid ?? offer.bands.min ?? offer.bands.max ?? 0).toLocaleString(L.dateLocale);
	y = addKeyValue(doc, L.offeredPrice, `${offerPrice} €`, margin, y, contentWidth);

	if (offer.validUntil) {
		y += 1;
		y = addKeyValue(doc, L.validUntil, formatDateShort(offer.validUntil, resolvePdfLanguage(language)), margin, y, contentWidth);
	}
	if (offer.projectStartDate) {
		y += 1;
		y = addKeyValue(doc, L.projectStart, formatDateShort(offer.projectStartDate, resolvePdfLanguage(language)), margin, y, contentWidth);
	}
	if (offer.timelineEstimate?.trim()) {
		y += 1;
		y = addKeyValue(doc, L.timelineEstimate, offer.timelineEstimate.trim(), margin, y, contentWidth);
	}
	if (offer.message?.trim()) {
		y += 1;
		y = addKeyValue(doc, L.message, offer.message.trim(), margin, y, contentWidth);
	}

	// Gantt
	const offerStartDate = offer.projectStartDate ? new Date(offer.projectStartDate) : new Date();
	let ganttTasks: GanttTask[] = [];
	if (offer.ganttData && offer.ganttData.length > 0) {
		ganttTasks = offer.ganttData;
	} else if (offer.timelineData && offer.timelineData.length > 0) {
		ganttTasks = offer.timelineData;
	} else {
		ganttTasks = ganttTasksFromPlanInstructions(aiContent.instructions, offerStartDate);
		if (!ganttTasks.length) {
			ganttTasks = generateSyntheticGanttData('garden', aiContent.pricingBreakdown?.mid ?? 0, offerStartDate);
		}
	}

	y += 8;
	if (ganttTasks.length > 0) {
		if (y + 20 > pageHeight - margin) {
			doc.addPage();
			y = margin + 22;
		}
		y = addSectionTitle(doc, L.ganttTimelineFromOffer, y);
		y += 5;
		addSimpleGanttChart(doc, ganttTasks, margin, y, contentWidth, pageHeight, margin, L);
	}

	// Update page numbers in headers
	const totalPages = doc.getNumberOfPages();
	const headerTitleY = 20;
	const headerLineY = 25;
	const firstPageNumberY = 35;
	for (let i = 1; i <= totalPages; i++) {
		doc.setPage(i);
		doc.setFontSize(10);
		doc.setTextColor(100, 100, 100);
		const pageNumberY = i === 1 ? firstPageNumberY : headerTitleY;
		if (i !== 1) {
			doc.text(docTitle, margin, headerTitleY);
			doc.setDrawColor(200, 200, 200);
			doc.line(margin, headerLineY, pageWidth - margin, headerLineY);
		}

		doc.text(L.pageOf(i, totalPages), pageWidth - margin, pageNumberY, { align: 'right' });
	}

	const datePart = toDatePartDDMMYYYY(aiContent.generatedAt || offer.createdAt);
	const ganttSeed = ganttTasks.map((t) => ({
		name: t.name,
		startDate: t.startDate,
		endDate: t.endDate,
		status: t.status,
		progress: t.progress
	}));
	const contentHash = hashStringToShort(
		stableStringify({
			offerId: offer.id,
			planId: plan.databaseId ?? plan.id,
			bands: offer.bands,
			message: offer.message,
			timelineEstimate: offer.timelineEstimate,
			validUntil: offer.validUntil,
			projectStartDate: offer.projectStartDate,
			ai: {
				generatedAt: aiContent.generatedAt,
				planDescription: aiContent.planDescription,
				keyFeatures: aiContent.keyFeatures,
				instructions: aiContent.instructions
			},
			gantt: ganttSeed
		}),
		8
	);
	const fileName = `HortEra-Offer-${offer.id}-${datePart}-${contentHash}.pdf`;
	doc.save(fileName);
}

/**
 * Generate a PDF for a project (offer accepted / in-progress / completed).
 * Focuses more on tasks and the project Gantt.
 */
export async function generateProjectPDF({
	projectTitle,
	projectStatusLabel,
	gardenVision,
	ganttTasks,
	offerMeta,
	client,
	companyName,
	budget,
	dueDate,
	beforeImageUrl,
	afterImageUrl,
	detailLevel = 'company',
	language
}: ProjectPDFOptions): Promise<void> {
	const L = getPdfLabels(language);
	const resolvedLanguage = resolvePdfLanguage(language);
	const doc = new jsPDF('p', 'mm', 'a4');
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	const margin = 14;
	const contentWidth = pageWidth - margin * 2;

	// Leave room for the PDF header on all pages (we draw header title + line around y=20..25).
	let y = margin + 18;
	const docTitle = projectTitle;
	const appHomepageUrl = getAppHomepageUrl();
	const appHomepageLinkText = L.tryHortEra;
	const generatedDate = new Date().toLocaleDateString(L.dateLocale);

	// Title Page
	doc.setFontSize(24);
	doc.setTextColor(25, 135, 84);
	doc.setFont('helvetica', 'bold');
	doc.text(docTitle, margin, y);

	y += 15;
	doc.setFontSize(12);
	doc.setTextColor(100, 100, 100);
	doc.setFont('helvetica', 'normal');
	doc.text(L.generatedOn(generatedDate), margin, y);
	addClickableAppHomepageLink(doc, {
		x: margin,
		y: y + 6,
		width: contentWidth,
		url: appHomepageUrl,
		linkText: appHomepageLinkText
	});
	y += 20;

	// Project Overview
	y = addSectionTitle(doc, L.projectOverview, y);
	y += 5;
	if (projectStatusLabel) {
		y = addKeyValue(doc, L.status, projectStatusLabel, margin, y, contentWidth);
	}

	if (typeof budget === 'number') {
		y = addKeyValue(doc, L.budget, `${budget.toLocaleString(L.dateLocale)} €`, margin, y + 1, contentWidth);
	}
	if (dueDate) {
		y = addKeyValue(doc, L.dueDate, formatDateShort(dueDate, resolvedLanguage), margin, y + 1, contentWidth);
	}
	if (client) {
		y = addKeyValue(doc, L.client, client, margin, y + 1, contentWidth);
	}
	if (companyName) {
		y = addKeyValue(doc, L.company, companyName, margin, y + 1, contentWidth);
	}

	if (offerMeta?.bands) {
		const offerPrice = (offerMeta.bands.mid ?? offerMeta.bands.min ?? offerMeta.bands.max ?? 0).toLocaleString(L.dateLocale);
		y += 3;
		y = addKeyValue(
			doc,
			L.offerPrice,
			`${offerPrice} €`,
			margin,
			y,
			contentWidth
		);
	}
	if (offerMeta?.validUntil) {
		y += 1;
		y = addKeyValue(doc, L.validUntil, formatDateShort(offerMeta.validUntil, resolvedLanguage), margin, y, contentWidth);
	}
	if (offerMeta?.projectStartDate) {
		y += 1;
		y = addKeyValue(doc, L.projectStart, formatDateShort(offerMeta.projectStartDate, resolvedLanguage), margin, y, contentWidth);
	}

	// Vision / Scope
	y += 6;
	if (y > pageHeight - 60) {
		doc.addPage();
		y = margin + 18;
	}
	y = addSectionTitle(doc, L.gardenVision, y);
	y += 5;

	doc.setFontSize(9);
	doc.setTextColor(100, 100, 100);
	doc.setFont('helvetica', 'normal');
	doc.text(L.description, margin, y);
	y += 5;
	y = addWrappedText(doc, gardenVision.planDescription, margin, y, contentWidth, 10);
	y += 8;

	// Key features
	if (y > pageHeight - 55) {
		doc.addPage();
		y = margin + 18;
	}
	y = addSectionTitle(doc, L.keyFeatures, y);
	y += 5;
	gardenVision.keyFeatures.forEach((f) => {
		if (y > pageHeight - 25) {
			doc.addPage();
			y = margin + 18;
		}
		doc.setFontSize(10);
		doc.setTextColor(25, 135, 84);
		doc.setFont('helvetica', 'bold');
		doc.text('✓', margin, y);
		doc.setTextColor(0, 0, 0);
		doc.setFont('helvetica', 'normal');
		y = addWrappedText(doc, f, margin + 5, y, contentWidth - 5, 10);
		y += 2;
	});

	// Implementation steps (condensed)
	y += 3;
	if (y > pageHeight - 60) {
		doc.addPage();
		y = margin + 18;
	}
	y = addSectionTitle(doc, L.implementationSteps, y);
	y += 5;
	gardenVision.instructions.slice(0, 10).forEach((instruction, idx) => {
		if (y > pageHeight - 25) {
			doc.addPage();
			y = margin + 18;
		}
		doc.setFontSize(10);
		doc.setTextColor(0, 0, 0);
		doc.setFont('helvetica', 'normal');
		y = addWrappedText(doc, `${idx + 1}. ${instruction}`, margin, y, contentWidth, 9);
		y += 2;
	});

	// Before / After images
	y += 6;
	const beforeAfterImgHeight = 60;
	const beforeAfterImgPadding = 10;
	if (beforeImageUrl || afterImageUrl) {
		if (y > pageHeight - 80) {
			doc.addPage();
			y = margin + 18;
		}
		y = addSectionTitle(doc, L.beforeAfter, y);
		y += 5;

		if (beforeImageUrl) {
			try {
				const beforeUrl = getImageUrl(beforeImageUrl);
				if (!beforeUrl) {
					// Skip image if we can't resolve a usable URL
				} else {
					const beforeBase64 = await imageToBase64(beforeUrl);
					if (beforeBase64) {
						doc.setFontSize(8);
						doc.setTextColor(100, 100, 100);
						doc.text(L.currentState, margin, y);
						y += 3;
						const imgWidth = (contentWidth - 5) / 2;
						doc.addImage(beforeBase64, 'JPEG', margin, y, imgWidth, beforeAfterImgHeight);
					}
				}
			} catch (error) {
				console.error('Failed to add before image:', error);
			}
		}

		const afterBaselineY = y;
		if (afterImageUrl) {
			try {
				const afterUrlResolved = afterImageUrl;
				const afterUrl = getImageUrl(afterUrlResolved);
				if (!afterUrl) {
					// Skip image if we can't resolve a usable URL
				} else {
					const afterBase64 = await imageToBase64(afterUrl);
					if (afterBase64) {
						doc.setFontSize(8);
						doc.setTextColor(100, 100, 100);
						doc.text(L.proposedActualVision, pageWidth / 2 + 5, afterBaselineY - 3);
						const imgWidth = (contentWidth - 5) / 2;
						doc.addImage(afterBase64, 'JPEG', pageWidth / 2 + 5, afterBaselineY, imgWidth, beforeAfterImgHeight);
					}
				}
			} catch (error) {
				console.error('Failed to add after image:', error);
			}
		}

		y = afterBaselineY + beforeAfterImgHeight + beforeAfterImgPadding;
	}

	// Project Gantt
	y += 6;
	if (y > pageHeight - 60) {
		doc.addPage();
		y = margin + 18;
	}
	y = addSectionTitle(doc, L.projectGantt, y);
	y += 5;
	// addSimpleGanttChart paginates internally; capture the updated y to avoid overlaying the next section.
	y = addSimpleGanttChart(doc, ganttTasks, margin, y, contentWidth, pageHeight, margin, L);

	// Task list (top N)
	y += 6;
	if (y > pageHeight - 60) {
		doc.addPage();
		y = margin + 18;
	}
	y = addSectionTitle(doc, L.taskList, y);
	y += 5;
	const tasksSorted = [...ganttTasks].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
	const showAllTasks = detailLevel === 'company';
	const seekerMaxTasks = 12;
	const maxTasks = showAllTasks ? tasksSorted.length : seekerMaxTasks;

	for (let i = 0; i < tasksSorted.length && i < maxTasks; i++) {
		const t = tasksSorted[i];
		if (y > pageHeight - 25) {
			doc.addPage();
			y = margin + 18;
		}
		const statusRaw = (t.status ?? 'not_started').replace(/_/g, ' ').trim();
		const status = statusRaw.replace(/\b\w/g, (c) => c.toUpperCase());

		const depsCount = Array.isArray(t.dependencies) ? t.dependencies.length : 0;
		const progressPct = Math.round(t.progress ?? 0);

		const assigneesText = (() => {
			if (!showAllTasks) return '';
			const names = Array.isArray(t.gardenerNames) && t.gardenerNames.length > 0 ? t.gardenerNames : [];
			const ids = Array.isArray(t.gardenerIds) && t.gardenerIds.length > 0
				? t.gardenerIds.map(String)
				: (t.gardenerId ? [String(t.gardenerId)] : []);

			if (names.length > 0 && ids.length > 0) {
				const idPreview = ids.slice(0, 5).join(', ');
				return `${L.assignees} ${names.join(', ')} (IDs: ${idPreview}${ids.length > 5 ? '...' : ''})`;
			}
			if (names.length > 0) return `${L.assignees} ${names.join(', ')}`;
			if (ids.length > 0) {
				const idPreview = ids.slice(0, 5).join(', ');
				return `${L.assignees} ${ids.length} gardener(s) (IDs: ${idPreview}${ids.length > 5 ? '...' : ''})`;
			}
			return L.assigneesUnassigned;
		})();

		const metaLine1 = `${L.statusLabel} ${status} | ${formatDateShort(t.startDate, resolvedLanguage)} - ${formatDateShort(t.endDate, resolvedLanguage)} (${t.duration ?? 0}d)`;
		const metaLine2 = showAllTasks
			? `${L.dependencies} ${depsCount > 0 ? `${depsCount} task(s)` : L.dependenciesNone} | ${L.progress} ${progressPct}%`
			: '';
		doc.setFontSize(9);
		doc.setTextColor(0, 0, 0);
		doc.setFont('helvetica', 'bold');
		doc.text(`${t.name}`, margin, y);

		let metaY = y + 3;
		doc.setFont('helvetica', 'normal');
		doc.setTextColor(60, 60, 60);

		metaY = addWrappedText(doc, metaLine1, margin, metaY, contentWidth, showAllTasks ? 9 : 9);
		if (showAllTasks) {
			metaY = addWrappedText(doc, metaLine2, margin, metaY, contentWidth, 8.8);
			metaY = addWrappedText(doc, assigneesText, margin, metaY, contentWidth, 8.8);
		}

		// More spacing between task blocks improves readability in the PDF.
		y = metaY + (showAllTasks ? 3 : 2);
	}
	if (!showAllTasks && tasksSorted.length > maxTasks) {
		doc.setFontSize(9);
		doc.setTextColor(60, 60, 60);
		doc.text(L.moreTasks(tasksSorted.length - maxTasks), margin, y);
	}

	// Update page numbers in headers
	const totalPages = doc.getNumberOfPages();
	const headerTitleY = 20;
	const headerLineY = 25;
	const firstPageNumberY = 35;
	for (let i = 1; i <= totalPages; i++) {
		doc.setPage(i);
		doc.setFontSize(10);
		doc.setTextColor(100, 100, 100);
		const pageNumberY = i === 1 ? firstPageNumberY : headerTitleY;
		if (i !== 1) {
			doc.text(docTitle, margin, headerTitleY);
			doc.setDrawColor(200, 200, 200);
			doc.line(margin, headerLineY, pageWidth - margin, headerLineY);
		}

		doc.text(L.pageOf(i, totalPages), pageWidth - margin, pageNumberY, { align: 'right' });
	}

	const datePart = toDatePartDDMMYYYY(offerMeta?.projectStartDate || dueDate);
	const ganttSeed = ganttTasks.map((t) => ({
		name: t.name,
		startDate: t.startDate,
		endDate: t.endDate,
		status: t.status,
		progress: t.progress
	}));
	const contentHash = hashStringToShort(
		stableStringify({
			projectTitle,
			projectStatusLabel,
			offerMeta,
			budget,
			dueDate,
			gantt: ganttSeed
		}),
		8
	);
	const fileName = `HortEra-Project-${datePart}-${contentHash}.pdf`;
	doc.save(fileName);
}

